import { useEffect } from 'react';

import { useFilePreview } from 'api/requests';
import { ClientDocumentGroupData } from 'api/types/data/clientDocumentGroupData';
import { FileTypeName } from 'api/types/entity';
import { ErrorCode, ServerError } from 'api/types/error';
import { lodash, openAsyncLink } from 'helpers';
import { useObjectState } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import {
  SigningDocumentsFormType,
  FormErrors,
} from 'modules/onboarding/common/forms/signingDocumentsForm';

interface Args {
  documentGroup: ClientDocumentGroupData | undefined;
  submitError: ServerError | undefined;
  signDocumentGroup: (
    documentGroupId: string,
    otpCode: string
  ) => Promise<void>;
}

export const useSigningDocumentGroup = ({
  documentGroup,
  submitError,
  signDocumentGroup,
}: Args) => {
  const notify = useNotify();
  const { t } = useTranslation();

  const { getFileLink } = useFilePreview();

  const [formError, setFormError] = useObjectState<FormErrors>({});

  const documents = lodash.compact(documentGroup?.files.map((f) => f.type));

  const onClickDocument = async (type: FileTypeName) => {
    if (type) {
      const doc = documentGroup!.files.find((t) => t.type === type);

      if (doc) {
        openAsyncLink(() => getFileLink(doc.id));
      }
    }
  };

  const onSubmit = async (values: SigningDocumentsFormType) => {
    if (values.otpCode) {
      await signDocumentGroup(documentGroup!.id!, values.otpCode);
      notify.info(t('onboarding.signingDocumentsWrapper.successMessage'));
    }
  };

  useEffect(() => {
    if (submitError) {
      if (submitError.code === ErrorCode.INVALID_OTP_TOKEN) {
        setFormError({ otpCode: submitError.message });
      } else {
        setFormError({ otpCode: undefined });
        notify.error(submitError);
      }
    } else {
      setFormError({ otpCode: undefined });
    }
  }, [notify, setFormError, submitError]);

  return {
    documents,
    formError,
    onClickDocument,
    onSubmit,
  };
};
